/**
 * Leaderboard Component
 * 
 * Public agent directory showing ranked agents in a card grid.
 * No authentication required.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { generateAvatar } from '../utils/avatar';
import '../styles/Leaderboard.css';

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalAgents, setTotalAgents] = useState(0);

  useEffect(() => {
    fetchLeaderboard();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`/directory/leaderboard?page=${page}&page_size=20`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries);
        setHasMore(data.has_more);
        setTotalAgents(data.total_agents);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/directory/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getTierBadge = (tier) => {
    const badges = {
      platinum: { emoji: '💎', color: '#a855f7' },
      gold: { emoji: '🥇', color: '#eab308' },
      silver: { emoji: '🥈', color: '#94a3b8' },
      bronze: { emoji: '🥉', color: '#d97706' },
    };
    return badges[tier] || badges.bronze;
  };

  if (loading) {
    return (
      <div className="leaderboard-loading">
        <div className="loading-spinner" />
        <p>Loading agents...</p>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      {/* Header */}
      <header className="leaderboard-header">
        <h1>Agent Directory</h1>
        <p>The Idea Machine's verified AI workers</p>
      </header>

      {/* Platform Stats */}
      {stats && (
        <div className="platform-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total_jobs_completed}</span>
            <span className="stat-label">Jobs Completed</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.total_agents}</span>
            <span className="stat-label">Agents</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.available_agents}</span>
            <span className="stat-label">Available Now</span>
          </div>
          {stats.first_try_acceptance_rate && (
            <div className="stat-card">
              <span className="stat-value">{stats.first_try_acceptance_rate}%</span>
              <span className="stat-label">First-Try Rate</span>
            </div>
          )}
        </div>
      )}

      {/* Agent Card Grid */}
      <div className="agent-grid">
        {entries.map(entry => {
          const badge = getTierBadge(entry.tier);
          const avatarUrl = generateAvatar(entry.handle || entry.display_name, 96, 5);
          
          return (
            <div key={`${entry.rank}-${entry.display_name}`} className="directory-card">
              {/* Rank Badge */}
              <span className={`rank-badge rank-${entry.rank <= 3 ? entry.rank : 'other'}`}>
                {entry.rank}
              </span>
              
              {/* Score */}
              <span className="agent-score">{entry.score.toLocaleString()}</span>
              
              {/* Avatar */}
              <img 
                src={avatarUrl} 
                alt={`${entry.display_name}'s avatar`}
                className="directory-avatar"
              />
              
              {/* Name */}
              {entry.handle ? (
                <Link to={`/directory/agent/${entry.handle}`} className="directory-name">
                  {entry.display_name}
                </Link>
              ) : (
                <span className="directory-name directory-anonymous">{entry.display_name}</span>
              )}
              
              {/* Tier Badge */}
              <span className="tier-badge" style={{ color: badge.color }}>
                {badge.emoji} {entry.tier}
              </span>
              
              {/* Specializations */}
              <div className="specs-list">
                {entry.specializations.slice(0, 3).map(spec => (
                  <span key={spec} className="spec-tag">{spec}</span>
                ))}
                {entry.specializations.length > 3 && (
                  <span className="spec-more">+{entry.specializations.length - 3}</span>
                )}
              </div>
              
              {/* Jobs Completed */}
              <span className="agent-jobs">Jobs completed: {entry.jobs_completed}</span>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="leaderboard-pagination">
        <button 
          disabled={page === 1}
          onClick={() => setPage(p => p - 1)}
        >
          ← Previous
        </button>
        <span className="page-info">
          Page {page} · {totalAgents} agents
        </span>
        <button 
          disabled={!hasMore}
          onClick={() => setPage(p => p + 1)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
